import type { SupabaseClient } from "@supabase/supabase-js";
import { withBase } from "./base";

/**
 * The browser half of the R2 resource store (worker/resources.ts).
 *
 * Every call carries the Supabase access token, because the Worker re-reads the
 * caller's role from `profiles` rather than trusting anything the client says
 * about itself. Reads are the exception: those use a signed URL the Worker
 * mints here, so that an <img>, a <video> or a download link — none of which
 * can send a header — still work.
 */

export type UploadedFile = {
  key: string;
  size: number;
  mimeType: string;
  fileName: string | null;
  url: string;
};

async function accessToken(supabase: SupabaseClient) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Your session has expired. Sign in again.");
  return token;
}

async function failure(response: Response, fallback: string) {
  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  return new Error(body?.error || fallback);
}

/**
 * Upload one file and get back its key.
 *
 * The body is the file itself rather than multipart form data: there is exactly
 * one part, and `request.arrayBuffer()` in the Worker is both simpler and
 * cheaper than parsing a form for it. The name rides in a header because a file
 * name can be anything, including a newline.
 */
export async function uploadFile(
  supabase: SupabaseClient,
  file: File,
  prefix: "activities" | "suggestions" = "activities",
): Promise<UploadedFile> {
  const token = await accessToken(supabase);
  const response = await fetch(withBase(`/api/resources/upload?prefix=${prefix}`), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": file.type || "application/octet-stream",
      // Headers are latin-1; a worksheet named in Urdu would otherwise throw
      // before the request left the browser.
      "X-File-Name": encodeURIComponent(file.name),
    },
    body: file,
  });
  if (!response.ok) throw await failure(response, "That file did not upload.");
  return (await response.json()) as UploadedFile;
}

/**
 * Signed URLs for a batch of keys.
 *
 * Batched because a page of resources is a page of files: one round trip for
 * twelve worksheets, not twelve. Keys the caller may not read are simply absent
 * from the result rather than erroring the whole batch — one retired file
 * should not blank a page.
 */
export async function signKeys(
  supabase: SupabaseClient,
  keys: string[],
): Promise<Record<string, string>> {
  const wanted = [...new Set(keys)].filter(Boolean);
  if (wanted.length === 0) return {};

  const token = await accessToken(supabase);
  const signed: Record<string, string> = {};

  // The Worker caps a batch at 50 so one request cannot ask it to compute a
  // thousand HMACs.
  for (let index = 0; index < wanted.length; index += 50) {
    const response = await fetch(withBase("/api/resources/sign"), {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ keys: wanted.slice(index, index + 50) }),
    });
    if (!response.ok) throw await failure(response, "Could not open those files.");
    const body = (await response.json()) as { urls?: Record<string, string> };
    Object.assign(signed, body.urls ?? {});
  }

  // Signed paths are relative to the Worker, which is mounted on a sub-path.
  return Object.fromEntries(Object.entries(signed).map(([key, path]) => [key, withBase(path)]));
}

/** One signed URL, or null if the caller may not read it. */
export async function signKey(supabase: SupabaseClient, key: string) {
  const urls = await signKeys(supabase, [key]);
  return urls[key] ?? null;
}

export async function deleteFile(supabase: SupabaseClient, key: string) {
  const token = await accessToken(supabase);
  const response = await fetch(withBase("/api/resources/delete"), {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ key }),
  });
  if (!response.ok) throw await failure(response, "That file could not be removed.");
}

/** "2.4 MB" — a size a parent can judge before tapping it on mobile data. */
export function formatBytes(bytes: number | null) {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
